import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';

const s = {
  sidebar: {
    width: 192,
    minWidth: 192,
    background: '#fff',
    borderRight: '1px solid #e8eaed',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
  },
  group: { borderBottom: '1px solid #f0f2f5' },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#6b7a8d',
    userSelect: 'none',
    transition: 'background 0.15s',
  },
  groupBody: { padding: '2px 14px 10px' },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 0',
    cursor: 'pointer',
    fontSize: 13,
    color: '#3d4f63',
    borderRadius: 6,
    transition: 'color 0.15s',
  },
  checkboxInput: { accentColor: '#9b2c2c', width: 14, height: 14, cursor: 'pointer' },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    marginBottom: 6,
    background: '#f0f2f5',
    borderRadius: 6,
    padding: '4px 8px',
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: '#2d3a4a',
    fontSize: 12,
    width: '100%',
  },
  tahunSelect: {
    width: '100%',
    padding: '6px 8px',
    borderRadius: 7,
    border: '1px solid #dde1e7',
    fontSize: 13,
    color: '#2d3a4a',
    background: '#f8f9fb',
    cursor: 'pointer',
    outline: 'none',
  },
};

function FilterGroup({ label, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={s.group}>
      <div
        style={s.groupHeader}
        onClick={() => setOpen(o => !o)}
        onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fb')}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      >
        <span>{label}</span>
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </div>
      {open && <div style={s.groupBody}>{children}</div>}
    </div>
  );
}

function CheckList({ items = [], selected = [], onChange, onToggleAll, withSearch }) {
  const [q, setQ] = useState('');
  const allSelected = items.length > 0 && selected.length === items.length;
  const someSelected = selected.length > 0 && !allSelected;
  const allRef = useRef(null);
  const visible = withSearch && q.trim()
    ? items.filter(o => o.value.toLowerCase().includes(q.toLowerCase()))
    : items;

  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = someSelected;
  }, [someSelected]);

  return (
    <>
      {withSearch && (
        <div style={s.searchWrap}>
          <Search size={11} color="#9aa5b4" />
          <input
            style={s.searchInput}
            placeholder="Cari..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
      )}
      {!q.trim() && (
        <label style={{ ...s.checkbox, borderBottom: '1px solid #edf0f3', paddingBottom: 6, marginBottom: 2 }}>
          <input
            ref={allRef}
            type="checkbox"
            style={s.checkboxInput}
            checked={allSelected}
            onChange={() => onToggleAll(!allSelected)}
          />
          <span style={{ fontWeight: 700, color: '#2d3a4a' }}>All</span>
        </label>
      )}
      {visible.map(({ key, value }) => (
        <label key={key} style={s.checkbox}>
          <input
            type="checkbox"
            style={s.checkboxInput}
            checked={selected.includes(key)}
            onChange={() => onChange(key)}
          />
          {value}
        </label>
      ))}
    </>
  );
}

export default function Sidebar() {
  const { filterOptions, filterState, setters } = useDashboard();
  const { tahun, version, channel, divisi, salesOperation, typeSalesman, salesman } = filterState;
  const { setTahun, setVersion, setChannel, setDivisi, setSalesOperation, setTypeSalesman, setSalesman } = setters;

  const toggle = (setter, value) =>
    setter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);

  if (!filterOptions) return <aside style={s.sidebar} />;

  return (
    <aside style={s.sidebar}>
      <FilterGroup label="Periode">
        <select style={s.tahunSelect} value={tahun} onChange={e => setTahun(e.target.value)}>
          {filterOptions.tahun.map(({ key, value }) => <option key={key} value={key}>{value}</option>)}
        </select>
      </FilterGroup>

      {/* <FilterGroup label="Version">
        <select style={s.tahunSelect} value={version} onChange={e => setVersion(e.target.value)}>
          {filterOptions.version.map(({ key, value }) => <option key={key} value={key}>{value}</option>)}
        </select>
      </FilterGroup> */}

      <FilterGroup label="Channel">
        <CheckList items={filterOptions.channel} selected={channel} onChange={v => toggle(setChannel, v)}
          onToggleAll={all => setChannel(all ? filterOptions.channel.map(o => o.key) : [])} withSearch />
      </FilterGroup>

      <FilterGroup label="Divisi">
        <CheckList items={filterOptions.divisi} selected={divisi} onChange={v => toggle(setDivisi, v)}
          onToggleAll={all => setDivisi(all ? filterOptions.divisi.map(o => o.key) : [])} withSearch />
      </FilterGroup>

      <FilterGroup label="Sales Operation">
        <CheckList items={filterOptions.salesOperation} selected={salesOperation} onChange={v => toggle(setSalesOperation, v)}
          onToggleAll={all => setSalesOperation(all ? filterOptions.salesOperation.map(o => o.key) : [])} withSearch />
      </FilterGroup>

      <FilterGroup label="Type Salesman">
        <CheckList items={filterOptions.typeSalesman} selected={typeSalesman} onChange={v => toggle(setTypeSalesman, v)}
          onToggleAll={all => setTypeSalesman(all ? filterOptions.typeSalesman.map(o => o.key) : [])} withSearch />
      </FilterGroup>

      <FilterGroup label="Salesman">
        <CheckList items={filterOptions.salesman} selected={salesman} onChange={v => toggle(setSalesman, v)}
          onToggleAll={all => setSalesman(all ? filterOptions.salesman.map(o => o.key) : [])} withSearch />
      </FilterGroup>
    </aside>
  );
}
